import React, { useEffect, useState } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Stack, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import AfvalEilandMarkerSVG from './AfvalEilandMarkerSVG';
import AfvalHalteMarkerSVG from './AfvalHalteMarkerSVG';
import HarnasMarkerSVG from './HarnasMarkerSVG';

const typeList = [ 'kliko', '1100', 'afval eiland', 'afval halte', 'houten bak', 'harnas' ];
const streamList = [ 'pmd', 'waste', 'papier', 'glass', 'gft', 'folie', 'colson' ];

const MAP_WIDTH = 6592;
const MAP_HEIGHT = 2472;
const MAP_SRC = "/DV-R&C_2026-g.jpg";
const API_BASE = process.env.REACT_APP_API_BASE_URL;

function isPointInPolygon(point, polygon) { let x = point.x, y = point.y; let inside = false; for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) { let xi = polygon[i][0], yi = polygon[i][1]; let xj = polygon[j][0], yj = polygon[j][1]; let intersect = ((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi + 0.00001) + xi); if (intersect) inside = !inside; } return inside; }

function getZoneBoundingBox(points) { const xs = points.map(p => p[0]); const ys = points.map(p => p[1]); const minX = Math.min(...xs); const maxX = Math.max(...xs); const minY = Math.min(...ys); const maxY = Math.max(...ys); return { minX, maxX, minY, maxY }; }

function DataView() {
  const [typeCounts, setTypeCounts] = useState({});
  const [streamCounts, setStreamCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [zones, setZones] = useState([]);
  const [markers, setMarkers] = useState([]);
  const [selectedZone, setSelectedZone] = useState('all');

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetch(`${process.env.REACT_APP_API_BASE_URL}/api/zones`).then(res => res.json()),
      fetch(`${API_BASE}/api/markers`).then(res => res.json())
    ])
      .then(([zonesData, markersData]) => {
        setZones(zonesData);
        setMarkers(markersData);
        setLoading(false);
      })
      .catch(err => {
        setError('Failed to fetch data');
        setLoading(false);
      });
  }, []);

  // Compute filtered markers
  let filteredMarkers = markers;
  let selectedZoneObj = null;
  if (selectedZone !== 'all' && zones.length > 0) {
    selectedZoneObj = zones.find(z => z.uid === selectedZone);
    if (selectedZoneObj) {
      filteredMarkers = markers.filter(m => isPointInPolygon(m, selectedZoneObj.points));
    }
  }

  // Compute type and stream counts for filtered markers
  useEffect(() => {
    let counts = {};
    typeList.forEach(type => {
      counts[type] = filteredMarkers.filter(m => m.type === type).length;
    });
    counts['kliko'] += 2 * (counts['afval halte'] || 0) + 2 * (counts['afval eiland'] || 0);
    setTypeCounts(counts);

    let sCounts = {};
    streamList.forEach(stream => {
      sCounts[stream] = filteredMarkers.filter(m => m.stream === stream).length;
    });
    setStreamCounts(sCounts);
  }, [filteredMarkers]);

  // Map visual logic
  let viewBox = `0 0 ${MAP_WIDTH} ${MAP_HEIGHT}`;
  let highlightPolygon = null;
  let zoneMarkers = [];
  if (selectedZoneObj) {
    const { minX, maxX, minY, maxY } = getZoneBoundingBox(selectedZoneObj.points);
    const padX = Math.max(60, (maxX - minX) * 0.15);
    const padY = Math.max(60, (maxY - minY) * 0.15);
    const vMinX = Math.max(0, minX - padX);
    const vMinY = Math.max(0, minY - padY);
    const vWidth = Math.min(MAP_WIDTH - vMinX, (maxX - minX) + 2 * padX);
    const vHeight = Math.min(MAP_HEIGHT - vMinY, (maxY - minY) + 2 * padY);
    viewBox = `${vMinX} ${vMinY} ${vWidth} ${vHeight}`;
    highlightPolygon = (
      <polygon
        points={selectedZoneObj.points.map(p => p.join(",")).join(" ")}
        fill={selectedZoneObj.color ? selectedZoneObj.color + '33' : 'rgba(40,167,69,0.2)'}
        stroke={selectedZoneObj.color || '#28a745'}
        strokeWidth={6}
        style={{ filter: 'drop-shadow(0 0 12px #007bff)' }}
      />
    );
    zoneMarkers = filteredMarkers;
  }

  return (
      <div className='bg-[#202020]' style={{ minHeight: '100vh', width: '100%' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', width: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2, mb: 2, ml: 2 }}>
            <FormControl sx={{ minWidth: 240 }}>
              <InputLabel id="zone-select-label" sx={{ color: '#fff' }}>Zone</InputLabel>
              <Select
                sx={{
                  fill: '#fff',
                  color: '#fff',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#fff',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#fff',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#fff',
                  },
                }}
                labelId="zone-select-label"
                value={selectedZone}
                label="Zone"
                onChange={e => setSelectedZone(e.target.value)}
              >
                <MenuItem sx={{ color: '#fff' }} value="all">All (global totals)</MenuItem>
                {zones.map(zone => (
                  <MenuItem sx={{ color: '#fff' }} key={zone.uid} value={zone.uid}>{zone.name || 'Unnamed'}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        <Box
          sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh', width: '100%' }}
        >
          {loading ? (
            <Typography variant="body1" color="text.secondary" sx={{ mt: 3 }}>Loading...</Typography>
          ) : error ? (
            <Typography variant="body1" color="error.main" sx={{ mt: 3 }}>{error}</Typography>
          ) : (
            <>
              <div style={{ width: '100%' }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} mt={12} p={1} alignItems="flex-start" sx={{ width: '100%' }}>
                  <Box sx={{ minWidth: { xs: 340, sm: 340, md: 400 }, maxWidth: 600, background: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', flex: 1 }}>
                    <svg
                    
                      width="100%"
                      height="350"
                      viewBox={viewBox}
                      style={{ width: '100%', height: 350, background: '#eee', minHeight: 160 }}
                    >
                      <image
                        href={MAP_SRC}
                        x={0}
                        y={0}
                        width={MAP_WIDTH}
                        height={MAP_HEIGHT}
                        preserveAspectRatio="none"
                        style={{ pointerEvents: 'none' }}
                      />
                      {highlightPolygon}
                      {zoneMarkers.map((marker, idx) => {
                        const streamColors = {
                          pmd: '#007bff', // blue
                          waste: '#222', // black
                          papier: 'orange',
                          glass: 'red',
                          gft: 'green',
                          folie: 'gold',
                          colson: 'gray',
                        };
                        const fill = streamColors[marker.stream] || '#007bff';
                        let shape = null;
                        if (marker.type === '1100') {
                          shape = (
                            <rect
                              x={marker.x - 10}
                              y={marker.y - 6}
                              width={20}
                              height={12}
                              fill={fill}
                              style={{ pointerEvents: 'none' }}
                            />
                          );
                        } else if (marker.type === 'afval eiland') {
                          shape = <AfvalEilandMarkerSVG x={marker.x} y={marker.y} />;
                        } else if (marker.type === 'houten bak') {
                          shape = (
                            <rect
                              x={marker.x - 6}
                              y={marker.y - 6}
                              width={12}
                              height={12}
                              strokeWidth={4}
                              stroke={'#995d08'}
                              fill={fill}
                              style={{ pointerEvents: 'none' }}
                            />
                          );
                        } else if (marker.type === 'harnas') {
                          shape = <HarnasMarkerSVG x={marker.x} y={marker.y} stroke={fill} />;
                        } else if (marker.type === 'afval halte') {
                          shape = <AfvalHalteMarkerSVG x={marker.x} y={marker.y} />;
                        } else {
                          shape = (
                            <rect
                              x={marker.x - 5}
                              y={marker.y - 5}
                              width={10}
                              height={10}
                              fill={fill}
                              style={{ pointerEvents: 'none' }}
                            />
                          );
                        }
                        return (
                          <g key={idx}>
                            {shape}
                            {marker.type === 'tafelbeugels' && marker.tafelbeugelsAmount > 0 && (
                              <text
                                x={marker.x}
                                y={marker.y - 12}
                                textAnchor="middle"
                                fill="#007bff"
                                fontWeight="bold"
                                fontSize="19"
                                stroke="#fff"
                                strokeWidth="0.5"
                                style={{ paintOrder: 'stroke', userSelect: 'none', pointerEvents: 'none' }}
                              >
                                {marker.tafelbeugelsAmount + ' tafelbeugels'}
                              </text>
                            )}
                          </g>
                        );
                      })}
                    </svg>
                    {selectedZoneObj && (
                      <Typography variant="h6" sx={{ pb: 1 }}>
                        Zone {selectedZoneObj.name || 'Unnamed'}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ width: '100%' }}>
                    <TableContainer component={Paper} sx={{ width: '100%', mb: 2 }}>
                      <Table sx={{ width: '100%' }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {typeList.map(type => (
                            typeCounts[type] > 0 ? (
                              <TableRow key={type}>
                                <TableCell>{type.charAt(0).toUpperCase() + type.slice(1)}</TableCell>
                                <TableCell>{typeCounts[type]}</TableCell>
                              </TableRow>
                            ) : null
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <TableContainer component={Paper} sx={{ width: '100%' }}>
                      <Table sx={{ width: '100%' }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Stream</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {streamList.map(stream => (
                            streamCounts[stream] > 0 ? (
                              <TableRow key={stream}>
                                <TableCell>{stream.charAt(0).toUpperCase() + stream.slice(1)}</TableCell>
                                <TableCell>{streamCounts[stream]}</TableCell>
                              </TableRow>
                            ) : null
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Stack>
              </div>
            </>
          )}
        </Box>
      </Box>
      </div>
  );
}

export default DataView;
